import type { Theme } from '@material-ui/core'
import { makeStyles, Popover } from '@material-ui/core'
import type { NoteActivityDetails, User } from '@paintscout/api'
import React, { useState } from 'react'
import DeleteIcon from '@material-ui/icons/Delete'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import linkify from 'linkifyjs/html'
import CreateIcon from '@material-ui/icons/Create'
import type { QuoteFile } from 'paintscout'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import classNames from 'classnames'
import { formatName, getOwnerFromUser, contentHash } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util'
import { useTranslation } from '@paintscout/ui'
import Highlight from '@paintscout/ui/components/Highlight'
import Typography from '@paintscout/ui/components/Typography'
import HtmlContent from '@paintscout/ui/components/HtmlContent'
import { useIsMobile } from '@paintscout/ui/hooks'
import Link from '@paintscout/ui/components/Link/Link'
import { docTypeSwitch } from 'shared/util/docTypeSwitch'
import type { ActivityTypeComponentProps } from '../Activity'
import ActivityContent from '../ActivityContent'
import ActivityTitle from '../ActivityTitle'
import ActivityActions, { ActivityAction } from '../ActivityActions'
import NoteHistoryPopover from '../../NoteHistoryPopover'
import { formatRelativeTime } from '../../../util/formatRelativeTime'
import ActivityFooter from '../ActivityFooter'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'
import { useUsers } from '../../../providers/UsersProvider'
import { useUser } from '../../../providers/UserProvider'
import { useActivityTimeline } from '../../ActivityTimeline/ActivityTimelineContext'

const FIT_CONTENT = 'fit-content'
const COLOR_TRANSITION = 'color 0.2s ease-in-out'

// FontAwesome thumbtack (both solid — thumbtack-angle has no regular variant): a straight,
// pushed-in thumbtack when pinned, an angled (loose) thumbtack when not. See #21136.
function PinIcon({ active, className, style }: { active?: boolean; className?: string; style?: React.CSSProperties }) {
  return (
    <i className={classNames('fa-solid', active ? 'fa-thumbtack' : 'fa-thumbtack-angle', className)} style={style} />
  )
}

const useStyles = makeStyles<Theme, ActivityTypeComponentProps>((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingLeft: 0,
    paddingRight: 0,
    overflowWrap: 'break-word',
    paddingTop: 8
  },
  bubble: {
    position: 'relative',
    marginRight: 0,
    width: FIT_CONTENT,
    maxWidth: '100%',
    paddingRight: theme.spacing(3),
    '& a': {
      color: 'inherit'
    },
    // Linkified URLs in the note body: look like links and break instead of overflowing.
    '& a.note-link': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word'
    }
  },
  bubbleCorner: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderStyle: 'solid',
    borderWidth: '8px',
    borderColor: `#fff #fff ${theme.paintscout.palette.brand.Neutral.P80} ${theme.paintscout.palette.brand.Neutral.P80}`,
    ['-moz-border-radius']: '0 0 0 5px',
    borderRadius: '0 0 0 5px',
    '@media print': {
      display: 'none'
    }
  },
  note: {
    position: 'relative',
    '&:hover': {
      '& $actionsIconDiv': {
        visibility: 'visible',
        opacity: 1
      }
    }
  },
  noteContent: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: FIT_CONTENT,
    maxWidth: '100%',
    minWidth: 0,
    // Let the HtmlContent child shrink so long URLs wrap instead of overflowing the bubble.
    '& > div': {
      minWidth: 0,
      overflowWrap: 'anywhere',
      wordBreak: 'break-word'
    }
  },
  sentTime: {
    [theme.breakpoints.down('xs')]: {
      fontWeight: 600
    }
  },
  actionsIcon: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    '&:hover': {
      cursor: 'pointer',
      color: theme.paintscout.palette.brand.Neutral.P35,
      transition: COLOR_TRANSITION
    },
    [theme.breakpoints.down('md')]: {
      height: '1.4rem',
      width: '1.4rem'
    }
  },
  pinActionIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '1.25rem',
    color: theme.paintscout.palette.brand.Neutral.P70,
    '&:hover': {
      cursor: 'pointer',
      color: theme.paintscout.palette.brand.Neutral.P35,
      transition: COLOR_TRANSITION
    }
  },
  // Always-visible indicator shown in the title when a note is pinned (independent of hover).
  pinnedIndicator: {
    fontSize: '0.9rem',
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
    color: theme.palette.primary.main
  },
  fileAttachedIcon: {
    color: theme.paintscout.palette.brand.Neutral.P70,
    height: '1rem',
    marginLeft: 0,
    alignSelf: 'center',
    '&:hover': {
      cursor: 'pointer',
      color: theme.paintscout.palette.brand.Neutral.P35,
      transition: COLOR_TRANSITION
    }
  },
  actionsIconDiv: {
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 0.3s, visibility 0.3s',
    // Center all action icons on a shared row so the FontAwesome pin glyph (baseline-aligned)
    // lines up with the MUI edit/delete SVGs (middle-aligned). `gap` spaces whatever subset of
    // pin/edit/delete is rendered (pinning is decoupled from edit/delete). See #21136.
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    '&:last-child': {
      marginLeft: theme.spacing(2)
    },
    minWidth: FIT_CONTENT
  },
  moreFilesDiv: {
    '&:hover': {
      cursor: 'pointer',
      transition: COLOR_TRANSITION,
      '& *': {
        color: theme.paintscout.palette.brand.Neutral.P70
      }
    }
  },
  sentHistoryDiv: {
    cursor: 'pointer'
  },
  expandIcon: {
    color: theme.paintscout.palette.brand.Neutral.P80
  },
  updatedDiv: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  updatedDivHover: {
    cursor: 'pointer',
    '@media (hover: hover)': {
      '&:hover': {
        transition: COLOR_TRANSITION,
        '& *': {
          color: theme.paintscout.palette.brand.Neutral.P70
        }
      }
    }
  },
  activityActions: {
    marginTop: `-${theme.spacing(0.75)}px`,
    marginBottom: 0
  },
  activityFooter: {
    width: FIT_CONTENT,
    paddingRight: 0,
    paddingBottom: 2
  },
  link: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:visited, &:active, &:focus': {
      color: theme.palette.text.primary
    }
  }
}))

export default function NoteActivity(props: ActivityTypeComponentProps<NoteActivityDetails>) {
  const { activity, actions, onAction, disabled, disableLink } = props
  const { note, files, owner, trashed, timestamp, updated, docType, pinnedOn, translations } = activity.details
  const { lang, __ } = useTranslation()
  // Apply pre-baked machine translation of the note body (translations[lang][contentHash(note)]),
  // baked on save. Falls back to the original when absent or viewing in English. No-op in the
  // estimator app, which renders with lang 'en'.
  const displayNote =
    note && lang && lang !== 'en'
      ? (translations as Record<string, Record<string, string>> | undefined)?.[lang]?.[contentHash(note)] ?? note
      : note
  // Pinning is per-viewed-document: a note is pinned here only if its `pinnedOn` includes the
  // doc currently being viewed (provided by the timeline context). See #21136.
  const { pinnedDocId } = useActivityTimeline()
  const pinned = Boolean(pinnedDocId && pinnedOn?.includes(pinnedDocId))
  const { users } = useUsers()
  const { user } = useUser()
  const userMap = Object.values(users).reduce(
    (acc, user) => {
      acc[user.user_id] = user
      return acc
    },
    {} as { [key: string]: User }
  )
  const link = activity?.docIds?.[0] ? docTypeSwitch({ eventType: 'notes', docType, id: activity.docIds[0] }) : null
  const currentOwner = userMap[owner?.id] ? getOwnerFromUser(userMap[owner?.id]) : owner
  const classes = useStyles(props)
  const mobile = useIsMobile()
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  if (trashed) {
    return (
      <ActivityContent
        classes={{
          root: classes.content,
          bubble: classes.bubble
        }}
      >
        <div className={classes.message}>
          <em>{__('This message has been deleted')}</em>
        </div>
      </ActivityContent>
    )
  }
  const open = Boolean(popoverAnchorEl)
  function handleClick(e: React.MouseEvent) {
    setPopoverAnchorEl(e.currentTarget)
  }

  function handleClose() {
    setPopoverAnchorEl(null)
  }
  const totalEdits = activity?.details?.versions?.length ?? 0
  const hasEdits = totalEdits > 1

  const isOnPainterSchedule = location.pathname.includes('/scheduling/painter')

  const matchingLocationWhereNoteWasAdded =
    isOnPainterSchedule ||
    (docType === 'deal' && location.pathname.includes('/deals/')) ||
    (docType === 'contact' && location.pathname.includes('/contact/')) ||
    (docType === 'company' && location.pathname.includes('/companies/'))

  const shouldDisableLink = disableLink || isOnPainterSchedule || matchingLocationWhereNoteWasAdded

  // On the painter schedule the feed mixes in office/admin notes, so painters may only
  // edit/delete the notes they authored. Everywhere else (deal/contact/company feeds in the
  // estimator app) any editor keeps full access. See ENG-855.
  const isOwnNote = !!owner?.id && removeAuth0Prefix(user?.user_id) === removeAuth0Prefix(owner?.id)

  // Edit/Delete stay scoped to the doc where the note was created.
  const showActions = !disabled && actions && matchingLocationWhereNoteWasAdded && (!isOnPainterSchedule || isOwnNote)

  // Pinning is decoupled from edit/delete: a note can be pinned on ANY doc it appears on,
  // regardless of where it was created. `pinnedDocId` is set (by NotesDocList) only on the
  // deal/contact/company feeds, and is the doc currently being viewed. See #21136.
  const showPinAction = !disabled && !!onAction && !!pinnedDocId

  function handlePinToggle(e: React.MouseEvent) {
    e.stopPropagation()
    e.preventDefault()
    if (onAction) {
      onAction(pinned ? 'unpin' : 'pin', activity)
    }
  }

  function handleLinkClick(e: React.MouseEvent) {
    // Linkified URLs in the note body are nested inside this row <Link>. React Router would
    // otherwise consume the click and navigate to the note's doc, so open the URL ourselves
    // and stop the row navigation. (react-router calls this onClick before deciding to navigate,
    // and skips navigation when the event was default-prevented.)
    const noteLink = (e.target as HTMLElement).closest?.('a.note-link') as HTMLAnchorElement | null
    if (noteLink) {
      e.preventDefault()
      window.open(noteLink.href, '_blank', 'noopener,noreferrer')
      return
    }
    if ((e.target as HTMLElement).closest('.no-redirect')) {
      e.preventDefault()
    }
  }

  return (
    <div className={classes.note}>
      <ActivityTitle
        rightContent={
          (showPinAction || showActions) &&
          (mobile ? (
            <ActivityActions className={classes.activityActions}>
              {showPinAction && (
                <ActivityAction
                  onClick={() => {
                    if (onAction) {
                      onAction(pinned ? 'unpin' : 'pin', activity)
                    }
                  }}
                  icon={(iconProps) => (
                    // Wrap in a 24x24 box (matching the MUI edit/delete SVGs) so the glyph aligns
                    // with the other menu icons; hard-code its size so it ignores the MenuItem font.
                    <span
                      className={iconProps?.className}
                      style={{
                        width: 24,
                        height: 24,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PinIcon active={pinned} style={{ fontSize: 16 }} />
                    </span>
                  )}
                >
                  {pinned ? __('Unpin') : __('Pin')}
                </ActivityAction>
              )}
              {showActions && (
                <ActivityAction
                  onClick={() => {
                    if (onAction) {
                      onAction('edit', activity)
                    }
                  }}
                  icon={CreateIcon}
                >
                  {__('Edit')}
                </ActivityAction>
              )}
              {showActions && (
                <ActivityAction
                  onClick={() => {
                    if (onAction) {
                      onAction('delete', activity)
                    }
                  }}
                  icon={DeleteIcon}
                >
                  {__('Delete')}
                </ActivityAction>
              )}
            </ActivityActions>
          ) : (
            <div id={'actions-icon-div'} className={classes.actionsIconDiv}>
              {showPinAction && (
                <span
                  className={classes.pinActionIcon}
                  onClick={handlePinToggle}
                  title={pinned ? __('Unpin note') : __('Pin note')}
                >
                  <PinIcon active={pinned} />
                </span>
              )}
              {showActions && (
                <CreateIcon
                  className={classes.actionsIcon}
                  onClick={() => {
                    if (onAction) {
                      onAction('edit', activity)
                    }
                  }}
                />
              )}
              {showActions && (
                <DeleteIcon
                  className={classes.actionsIcon}
                  onClick={() => {
                    if (onAction) {
                      onAction('delete', activity)
                    }
                  }}
                />
              )}
            </div>
          ))
        }
      >
        {shouldDisableLink ? (
          <span>
            {pinned && <PinIcon active className={classes.pinnedIndicator} />}
            <Highlight>{__('Note')}</Highlight> {__('added by')} <Highlight>{formatName(currentOwner)}</Highlight>
          </span>
        ) : (
          <Link to={link} classes={{ root: classes.link }} onClick={handleLinkClick}>
            {pinned && <PinIcon active className={classes.pinnedIndicator} />}
            <Highlight>{__('Note')}</Highlight> {__('added by')} <Highlight>{formatName(currentOwner)}</Highlight>
          </Link>
        )}
      </ActivityTitle>
      {(() => {
        const innerContent = (
          <>
            <ActivityContent
              classes={{
                root: classes.content,
                bubble: classes.bubble
              }}
            >
              <div className={classes.bubbleCorner} />
              <div className={classes.noteContent}>
                <HtmlContent
                  content={linkify(displayNote, {
                    target: '_blank',
                    className: 'note-link',
                    attributes: { rel: 'noopener noreferrer' }
                  })}
                />
                {files?.length > 0 && (
                  <AttachFileIcon
                    className={classes.fileAttachedIcon}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      if (onAction) {
                        onAction(`edit-file`, activity)
                      }
                    }}
                  />
                )}
              </div>
            </ActivityContent>
            {activity?.details?.legacyNote && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontStyle: 'italic', fontSize: '0.7rem', marginTop: '-5px', marginBottom: '5px' }}
              >
                {__('This note was automatically carried over from previous contact edit form.')}
              </Typography>
            )}
            {onAction && (
              <>
                {[...(files?.slice(0, 3) ?? [])]?.map((file) => {
                  const fullFile: QuoteFile = onAction(`file_${file}`, activity)
                  if (!fullFile || !fullFile.title) {
                    return null
                  }
                  const fileExtension = fullFile?.type?.includes('/')
                    ? fullFile?.type.split('/')[1]
                    : fullFile?.type ?? 'unknown'
                  const title = fullFile.title
                  const displayName = title.includes(fileExtension) ? title : `${title}.${fileExtension}`
                  return (
                    <Typography key={file} variant="body2" color="textSecondary">
                      {displayName}
                    </Typography>
                  )
                })}
                {files?.length > 3 && (
                  <div
                    className={classes.fileAttachedIcon}
                    onClick={() => {
                      if (onAction) {
                        onAction(`edit-file`, activity)
                      }
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {__('+ %d more files', files?.length - 3)}
                    </Typography>
                  </div>
                )}
              </>
            )}
            <QuoteActivitySubtitle activity={activity} showDocTypeDetails={!matchingLocationWhereNoteWasAdded} />
          </>
        )
        return shouldDisableLink ? (
          <div>{innerContent}</div>
        ) : (
          <Link to={link} classes={{ root: classes.link }} onClick={handleLinkClick}>
            {innerContent}
          </Link>
        )
      })()}
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <ActivityFooter className={classes.activityFooter} timestamp={timestamp} showDate={pinned} />
        {updated && hasEdits && (
          <>
            <div
              onClick={hasEdits ? handleClick : null}
              className={classNames({
                [classes.sentHistoryDiv]: hasEdits,
                [classes.updatedDiv]: true,
                [classes.updatedDivHover]: onAction
              })}
            >
              <Typography className={classes.sentTime} variant={'subtitle2'}>
                {`- ${__('Updated %s', formatRelativeTime(updated, lang))}`}
              </Typography>
              {onAction && <ExpandMoreIcon className={classes.expandIcon} />}
            </div>
            <Popover
              elevation={1}
              open={open}
              anchorEl={popoverAnchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                horizontal: -14,
                vertical: 0
              }}
            >
              <NoteHistoryPopover note={activity} totalEdits={totalEdits} onNoteClick={handleClose} />
            </Popover>
          </>
        )}
      </div>
    </div>
  )
}
